import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, IconButton, InputAdornment, TextField, Button } from '@mui/material';
import { Add, Remove, ShoppingCartOutlined, Search, InfoOutlined } from '@mui/icons-material';
import api from '../../../../api/axios';
import { useCurrency } from '../../../../hooks/useCurrency';
import { moneyFromMinor, moneyToMinor } from '../../../utils/UnitConversion.utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { toast } from "react-toastify";
import { SteamAccount } from "../../../../store/slices/authSlice";
import SteamAccountUserSelect from '../../../utils/SteamAccountUserSelect';
import BuyOrderPriceIncrementsModal from './BuyOrderPriceIncrementsModal';

interface Props {
  itemName: string;
  referencePriceMinor?: number;
  onSuccess?: () => void;
  selectedSteamAccount: SteamAccount | null;
  onSteamAccountChange: (account: SteamAccount | null) => void;
  initialData?: {
    priceMinor: number;
    quantity: number;
    steamAccount: SteamAccount;
    orderId?: number;
  };
}

interface BuyOrderGroupDTO {
  priceMinor: number;
  quantity: number;
}

interface MarketOrdersResponse {
  referencePriceMinor: number | null;
  orders: BuyOrderGroupDTO[];
}

const SimpleBuyOrder: React.FC<Props> = ({
                                           itemName: initialItemName,
                                           referencePriceMinor: propsReferencePrice,
                                           onSuccess,
                                           selectedSteamAccount,
                                           onSteamAccountChange,
                                           initialData
                                         }) => {
  const { convertFromUSD, convertToUSD, currency, formatAmountWithoutCurrency } = useCurrency();
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);

  const steamAccountAmount = useSelector((state: RootState) => state.auth.steamAccountAmount || 0);
  const availableAccounts = useSelector((state: RootState) => state.auth.steamAccounts || []);

  const [value, setValue] = useState<string | null>(initialItemName || null);
  const [inputValue, setInputValue] = useState(initialItemName || '');
  const [options, setOptions] = useState<string[]>(initialItemName ? [initialItemName] : []);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [quantity, setQuantity] = useState<number>(initialData?.quantity || 1);
  const [priceInCents, setPriceInCents] = useState<number>(initialData?.priceMinor || 0);
  const [displayPrice, setDisplayPrice] = useState<string>('0.00');
  const [isTyping, setIsTyping] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const [marketData, setMarketData] = useState<MarketOrdersResponse>({
    referencePriceMinor: null,
    orders: []
  });

  const isEditMode = !!initialData;

  useEffect(() => {
    if (!selectedSteamAccount && availableAccounts.length > 0) {
      onSteamAccountChange(availableAccounts[0]);
    }
  }, [availableAccounts, selectedSteamAccount, onSteamAccountChange]);

  const getIncrementInCents = (cents: number): number => {
    if (cents < 500) return 1;
    if (cents < 1000) return 5;
    if (cents < 10000) return 10;
    if (cents < 50000) return 100;
    if (cents < 100000) return 500;
    return 1000;
  };

  const snapToStep = (cents: number): number => {
    if (cents < 2) return 2;
    const step = getIncrementInCents(cents);
    return Math.round(cents / step) * step;
  };

  const syncDisplayPrice = (cents: number) => {
    const priceInUSD = moneyFromMinor(cents);
    const priceInUserCurrency = convertFromUSD(priceInUSD);
    setDisplayPrice(formatAmountWithoutCurrency(priceInUserCurrency));
  };

  useEffect(() => {
    if (!isTyping) {
      syncDisplayPrice(priceInCents);
    }
  }, [priceInCents, isTyping, convertFromUSD]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue.trim().length >= 2 && inputValue !== value) {
        fetchSuggestions(inputValue);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, value]);

  useEffect(() => {
    if (value) fetchMarketData(value);
  }, [value]);

  const fetchSuggestions = async (query: string) => {
    setLoading(true);
    try {
      const response = await api.get<string[]>(`/buy-orders/search-item`, { params: { itemName: query } });
      setOptions(value ? Array.from(new Set([value, ...response.data])) : response.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchMarketData = async (itemName: string) => {
    try {
      const response = await api.get<MarketOrdersResponse>(`/buy-orders`, { params: { itemName } });
      const data = response.data;
      setMarketData(data);

      if (!isEditMode) {
        if (data.orders.length > 0) {
          const bestPrice = data.orders[0].priceMinor;
          const step = getIncrementInCents(bestPrice);
          setPriceInCents(Math.max(2, bestPrice - step));
        } else {
          const finalRef = data.referencePriceMinor ?? propsReferencePrice ?? 0;
          setPriceInCents(snapToStep(Math.max(2, finalRef)));
        }
      }
    } catch (e) { console.error(e); }
  };

  const handleManualPriceChange = (val: string) => {
    setIsTyping(true);
    const sanitized = val.replace(/[^0-9.,]/g, '');
    setDisplayPrice(sanitized);

    const amountInUserCurrency = parseFloat(sanitized.replace(',', '.'));
    if (!isNaN(amountInUserCurrency)) {
      const amountInUSD = convertToUSD(amountInUserCurrency);
      setPriceInCents(moneyToMinor(amountInUSD));
    }
  };

  const handleBlur = () => {
    setIsTyping(false);
    const snapped = snapToStep(priceInCents);
    setPriceInCents(snapped);
    syncDisplayPrice(snapped);
  };

  const adjustPrice = (direction: 'up' | 'down') => {
    setIsTyping(false);
    const step = getIncrementInCents(priceInCents);
    const nextVal = direction === 'up' ? priceInCents + step : priceInCents - step;
    const snapped = snapToStep(nextVal);
    setPriceInCents(snapped);
    syncDisplayPrice(snapped);
  };

  const handlePlaceOrder = async () => {
    if (!value || priceInCents < 2 || isSubmitting || !selectedSteamAccount) {
      if (!selectedSteamAccount) toast.warn("Please select a Steam Account.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        itemName: value,
        priceMinor: priceInCents,
        quantity: quantity,
        steamAccountId: selectedSteamAccount.id
      };

      if (isEditMode && initialData?.orderId) {
        await api.put(`/buy-orders/${initialData.orderId}`, payload);
        toast.success("Buy Order successfully updated!");
      } else {
        await api.post('/buy-orders', payload);
        toast.success("Buy Order successfully placed!");
      }

      await fetchMarketData(value);
      if (onSuccess) onSuccess();
    } catch (e) {
      console.error('Failed to process order', e);
      toast.error(isEditMode ? "Failed to update buy order." : "Failed to create buy order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSuccess = value !== null && value === inputValue;
  const isError = inputValue.length > 0 && value !== inputValue && !loading;

  return (
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-4 items-stretch h-auto lg:h-[400px]">
        <div className="flex-1 flex flex-col justify-between space-y-6 lg:space-y-0">
          <div className="space-y-4">
            <div className="relative group">
              <Autocomplete
                  options={options}
                  loading={loading}
                  value={value}
                  disabled={isSubmitting || isEditMode}
                  noOptionsText={inputValue.length < 2 ? "Search for Item..." : "No items found"}
                  onChange={(_, nv) => setValue(nv)}
                  inputValue={inputValue}
                  onInputChange={(_, nv, reason) => {
                    if (reason === 'input') {
                      setInputValue(nv);
                      if (nv !== value) setValue(null);
                    } else if (reason === 'reset') {
                      setInputValue(nv || '');
                    }
                  }}
                  componentsProps={{
                    paper: {
                      sx: { borderRadius: 0, border: '1px solid rgba(255,255,255,0.05)', boxShadow: 'none' }
                    }
                  }}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          label="Search Item"
                          variant="filled"
                          fullWidth
                          error={isError}
                          className="!bg-neutral-900 overflow-hidden"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            sx: {
                              borderRadius: 0,
                              height: 56,
                              display: 'flex',
                              alignItems: 'center',
                              '& .MuiFilledInput-input': {
                                paddingTop: '20px',
                              }
                            },
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    sx={{
                                      marginTop: '0 !important',
                                      alignSelf: 'center'
                                    }}
                                >
                                  <Search className="text-neutral-500" sx={{ fontSize: 20, ml: 1 }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <div className="flex items-center gap-2 mr-2">
                                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </div>
                            ),
                          }}
                      />
                  )}
              />
              <div className={`absolute bottom-0 left-0 h-[2px] w-full transition-all duration-300 ${isSuccess ? 'bg-emerald-500' : isError ? 'bg-red-500' : 'bg-neutral-800'}`} />
            </div>

            <div className="grid grid-cols-12 gap-3">
              {/* Quantity Field */}
              <div className="col-span-12 xs:col-span-4 flex flex-col">
                <div className="flex items-center ml-1 h-[20px]">
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                    Quantity
                  </label>
                </div>
                <TextField
                    // Changed type to text to remove browser arrows, inputMode handles numeric keyboard
                    type="text"
                    inputMode="numeric"
                    disabled={isSubmitting}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(100, Math.max(1, parseInt(e.target.value.replace(/\D/g, '')) || 1)))}
                    fullWidth
                    variant="filled"
                    className="!bg-neutral-900 border border-neutral-800 overflow-hidden"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: 0,
                        height: 48,
                        '& .MuiFilledInput-input': {
                          paddingTop: '12px',
                          paddingBottom: '12px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }
                    }}
                />
              </div>

              {/* Price Field */}
              <div className="col-span-12 xs:col-span-8 flex flex-col">
                <div className="flex items-center justify-between ml-1 h-[20px]">
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                    Max Price ({currency})
                  </label>
                  <IconButton
                      size="small"
                      onClick={() => setIsInfoModalOpen(true)}
                      className="!p-0 !text-neutral-600 hover:!text-neutral-300"
                  >
                    <InfoOutlined sx={{ fontSize: 14 }} />
                  </IconButton>
                </div>
                <TextField
                    value={displayPrice}
                    disabled={isSubmitting}
                    onChange={(e) => handleManualPriceChange(e.target.value)}
                    onBlur={handleBlur}
                    variant="filled"
                    fullWidth
                    className="!bg-neutral-900 border border-neutral-800 overflow-hidden"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: 0,
                        height: 48,
                        '& .MuiFilledInput-input': {
                          paddingTop: '12px',
                          paddingBottom: '12px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      },
                      startAdornment: (
                          <InputAdornment position="start" sx={{ marginTop: '0 !important' }}>
                            <span className="text-neutral-500 text-sm">{currencySymbol}</span>
                          </InputAdornment>
                      ),
                      endAdornment: (
                          <InputAdornment position="end" sx={{ marginTop: '0 !important' }}>
                            <div className="flex items-center">
                              <IconButton onClick={() => adjustPrice('down')} disabled={isSubmitting} size="small" className="!text-neutral-500 hover:!text-white">
                                <Remove fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => adjustPrice('up')} disabled={isSubmitting} size="small" className="!text-neutral-500 hover:!text-white">
                                <Add fontSize="small" />
                              </IconButton>
                            </div>
                          </InputAdornment>
                      )
                    }}
                />
              </div>
            </div>

            {steamAccountAmount > 1 && (
                <div className="flex flex-col gap-1.5 mt-2">
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold ml-1">
                    Steam Account
                  </label>
                  <div className="bg-neutral-900 border border-neutral-800 overflow-hidden" style={{ borderRadius: 0 }}>
                    <SteamAccountUserSelect
                        value={selectedSteamAccount}
                        size="small"
                        variant="outlined"
                        placeholder={false}
                        showAvatar={true}
                        onChange={(account) => onSteamAccountChange(account)}
                    />
                  </div>
                </div>
            )}
          </div>

          <div className="space-y-4 pt-4">
            <div className={`p-3 flex items-center gap-3 border transition-colors ${isSuccess ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-neutral-800/40 border-neutral-800'}`} style={{ borderRadius: 0 }}>
              <ShoppingCartOutlined className={isSuccess ? 'text-emerald-500' : 'text-neutral-600'} sx={{ fontSize: 18 }} />
              <span className="text-[11px] font-medium tracking-wide text-neutral-400">
              {isSuccess ? <span>Ready to {isEditMode ? 'update' : 'create'} buy order for <span className="text-emerald-500">{value}</span></span> : "Search for an item to continue"}
            </span>
            </div>

            <Button
                fullWidth
                variant="contained"
                disabled={!isSuccess || priceInCents < 2 || isSubmitting || !selectedSteamAccount}
                onClick={handlePlaceOrder}
                color="primary"
                sx={{
                  height: 48,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: 0
                }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? "Update Order" : "Place Order")}
            </Button>
          </div>
        </div>

        {/* Sidebar Market Table */}
        <div className="w-full lg:w-64 bg-neutral-900/50 border border-neutral-800 flex flex-col min-h-[200px] lg:h-full overflow-hidden" style={{ borderRadius: 0 }}>
          <div className="grid grid-cols-2 p-3 text-[10px] uppercase tracking-wider text-neutral-500 font-bold border-b border-neutral-800 bg-black/10">
            <span>Price ({currencySymbol})</span>
            <span className="text-right">Quantity</span>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-800 hover:scrollbar-thumb-neutral-700">
            {marketData.orders.length > 0 ? marketData.orders.slice(0, 10).map((order, i) => (
                <div key={i} className="grid grid-cols-2 px-3 py-2.5 text-xs border-b border-neutral-800/30 items-center hover:bg-white/[0.02] transition-colors">
              <span className="font-semibold text-neutral-200">
                {formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(order.priceMinor)))}
              </span>
                  <span className="text-right text-neutral-400 font-medium">{order.quantity}</span>
                </div>
            )) : (
                <div className="h-full flex flex-col items-center justify-center p-6 opacity-20">
                  <ShoppingCartOutlined sx={{ fontSize: 32, mb: 1, color: 'white' }} />
                  <span className="text-[10px] uppercase font-bold tracking-tighter text-white">No Active Orders</span>
                </div>
            )}
          </div>
        </div>

        {/* Info Modal Component */}
        <BuyOrderPriceIncrementsModal
            open={isInfoModalOpen}
            onClose={() => setIsInfoModalOpen(false)}
        />
      </div>
  );
};

export default SimpleBuyOrder;